import React, { useMemo, useState } from "react";
import { rsvpSession } from "../lib/api";
import { AddToCalendarButtons } from "./AddToCalendarButtons";
import { useAuth } from "../context/AuthContext";

/**
 * SessionInviteCard — renders inside the inbox feed for items of kind
 * `session_invite`. Provides three buttons (Accept · Maybe · Decline)
 * that call `POST /api/calendar/sessions/{id}/invites/{uid}/rsvp`
 * directly (bypassing the generic `decideInbox` accept/decline flow
 * because RSVP has a third option).
 *
 * After an Accept, the card flips to show a one-tap .ics download so
 * the user can drop the event in their personal calendar.
 *
 * The "decided" state is local — we don't reload the inbox feed on
 * each click because the parent re-loads after the toast dispatch.
 */
export function SessionInviteCard({ item, onRsvped }) {
  const p = item.payload || {};
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  // initial rsvp state — if the inbox row's status is already a
  // terminal value we render the appropriate label.
  const initial = useMemo(() => {
    if (["accept", "decline", "maybe"].includes(item.status)) return item.status;
    return "pending";
  }, [item.status]);
  const [rsvp, setRsvp] = useState(initial);

  const localStart = useMemo(() => formatTimezone(p.starts_at, user?.tz), [p.starts_at, user?.tz]);

  const submit = (choice) => async () => {
    if (!user || !p.session_id) return;
    setBusy(true);
    try {
      await rsvpSession(p.session_id, user.user_id, choice);
      setRsvp(choice);
      // Parent uses this to refresh the inbox feed (it expects an
      // "accept"/"decline" shape — "maybe" gets normalised to decline
      // for the soft-delete / toast path).
      onRsvped(choice === "decline" ? "decline" : "accept");
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("rsvp failed", e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid={`session-invite-card-${item.id}`}
      className="glass rounded-2xl p-3 border border-[#14b8a6]/30"
    >
      <div className="flex items-start gap-3">
        <div className="shrink-0 w-11 h-11 rounded-xl bg-[#14b8a6]/10 border border-[#14b8a6]/30 flex items-center justify-center">
          <i className="fas fa-calendar-bolt text-[#14b8a6] text-lg" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[9px] tracking-widest text-[#14b8a6] font-bold">
            <i className="fas fa-bolt mr-1" />SESSION INVITE
          </p>
          <p className="font-bold text-sm truncate">{p.title}</p>
          <p className="text-[11px] text-[#a3a3ac] truncate">
            {p.inviter_name && (<>From <strong>{p.inviter_name}</strong> · </>)}
            {p.game}
          </p>
          <p
            data-testid={`session-invite-when-${item.id}`}
            className="text-[11px] text-white/70 mt-1"
          >
            <i className="far fa-clock mr-1" />{localStart.long}
            <span className="text-white/35"> · host tz: {p.timezone || "UTC"}</span>
          </p>
          {p.notes && (
            <p className="mt-2 text-[11px] text-white/55 line-clamp-2">{p.notes}</p>
          )}
        </div>
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        {rsvp === "pending" ? (
          <>
            <button
              type="button"
              disabled={busy}
              onClick={submit("accept")}
              data-testid={`session-invite-accept-${item.id}`}
              className="text-[10px] tracking-widest px-3 py-1.5 rounded-lg bg-[#14b8a6] text-black hover:opacity-90 disabled:opacity-50"
            >
              <i className="fas fa-check mr-1" />ACCEPT
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={submit("maybe")}
              data-testid={`session-invite-maybe-${item.id}`}
              className="text-[10px] tracking-widest px-3 py-1.5 rounded-lg bg-white/5 text-white border border-white/10 hover:bg-white/10 disabled:opacity-50"
            >
              <i className="far fa-circle-question mr-1" />MAYBE
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={submit("decline")}
              data-testid={`session-invite-decline-${item.id}`}
              className="text-[10px] tracking-widest px-3 py-1.5 rounded-lg bg-white/5 text-white/70 border border-white/10 hover:bg-white/10 disabled:opacity-50"
            >
              <i className="fas fa-xmark mr-1" />DECLINE
            </button>
          </>
        ) : (
          <RsvpedState rsvp={rsvp} sess={p} testid={`session-invite-rsvped-${item.id}`} />
        )}
      </div>
    </div>
  );
}

function RsvpedState({ rsvp, sess, testid }) {
  const label = rsvp === "accept" ? "GOING" : rsvp === "maybe" ? "MAYBE" : "DECLINED";
  const cls = rsvp === "accept"
    ? "bg-[#14b8a6]/15 text-[#14b8a6] border-[#14b8a6]/40"
    : "bg-white/5 text-white/60 border-white/10";
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={testid}>
      <span className={`text-[10px] tracking-widest px-3 py-1.5 rounded-lg border ${cls}`}>
        <i className={`fas ${rsvp === "accept" ? "fa-circle-check" : rsvp === "maybe" ? "fa-circle-question" : "fa-xmark"} mr-1`} />
        {label}
      </span>
      {rsvp === "accept" && <AddToCalendarButtons sess={sess} />}
    </div>
  );
}

function formatTimezone(iso, tz) {
  if (!iso) return { long: "—" };
  const dt = new Date(iso);
  if (isNaN(dt)) return { long: iso };
  try {
    const long = dt.toLocaleString(undefined, {
      timeZone: tz || undefined,
      weekday: "short", month: "short", day: "numeric",
      hour: "numeric", minute: "2-digit",
      timeZoneName: "short",
    });
    return { long };
  } catch {
    return { long: dt.toString() };
  }
}

export default SessionInviteCard;
